import React from 'react';
import PropTypes from 'prop-types';

import {
  CartesianGrid,
  Label,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
  Scatter
} from 'recharts';

import style from './index.scss';

const Q4ReferenceLabel = (props) => {
  const { textAnchor, viewBox, dy, dx } = props;
  const xAxisValue = viewBox.width + viewBox.x;
  const yAxisValue = viewBox.height * 0.75;
  return (
    <text
      x={xAxisValue}
      y={yAxisValue}
      dy={dy}
      dx={dx}
      fill={props.fill}
      fontSize={12}
      textAnchor={textAnchor}>
      Q4
    </text>
  );
};

const Q3ReferenceLabel = (props) => {
  const { textAnchor, viewBox, dy, dx } = props;
  const xAxisValue = viewBox.width + viewBox.x;
  const yAxisValue = viewBox.height * 0.75;
  return (
    <text
      x={xAxisValue}
      y={yAxisValue}
      dy={dy}
      dx={dx}
      fill={props.fill}
      fontSize={12}
      textAnchor={textAnchor}>
      Q3
    </text>
  );
};

const Q2ReferenceLabel = (props) => {
  const { textAnchor, viewBox, dy, dx } = props;
  const xAxisValue = viewBox.width + viewBox.x;
  const yAxisValue = viewBox.height * 0.55;
  return (
    <text
      x={xAxisValue}
      y={yAxisValue}
      dy={dy}
      dx={dx}
      fill={props.fill}
      fontSize={12}
      textAnchor={textAnchor}>
      Q2
    </text>
  );
};

const Q1ReferenceLabel = (props) => {
  const { textAnchor, viewBox, dy, dx } = props;
  const xAxisValue = viewBox.width + viewBox.x;
  const yAxisValue = viewBox.height * 0.55;
  return (
    <text
      x={xAxisValue}
      y={yAxisValue}
      dy={dy}
      dx={dx}
      fill={props.fill}
      fontSize={12}
      textAnchor={textAnchor}>
      Q1
    </text>
  );
};

const TopLeftLabel = (props) => {
  const { textAnchor, viewBox, dy, dx } = props;
  const xAxisValue = viewBox.width + viewBox.x;
  const yAxisValue = viewBox.height * 0.1;
  return (
    <text
      x={xAxisValue}
      y={yAxisValue}
      dy={dy}
      dx={dx}
      fill={'#6A7088'}
      fontSize={10}
      textAnchor={textAnchor}>
      Low Risk, High Yield
    </text>
  );
};

const TopRightLabel = (props) => {
  const { textAnchor, viewBox, dy, dx } = props;
  const xAxisValue = viewBox.width + viewBox.x;
  const yAxisValue = viewBox.height * 0.1;
  return (
    <text
      x={xAxisValue}
      y={yAxisValue}
      dy={dy}
      dx={dx}
      fill={'#6A7088'}
      fontSize={10}
      textAnchor={textAnchor}>
      High Risk, High Yield
    </text>
  );
};

const BottomLeftLabel = (props) => {
  const { textAnchor, viewBox, dy, dx } = props;
  const xAxisValue = viewBox.width + viewBox.x;
  const yAxisValue = viewBox.height * 1.23;
  return (
    <text
      x={xAxisValue}
      y={yAxisValue}
      dy={dy}
      dx={dx}
      fill={'#6A7088'}
      fontSize={10}
      textAnchor={textAnchor}>
      Low Risk, Low Yield
    </text>
  );
};

const BottomRightLabel = (props) => {
  const { textAnchor, viewBox, dy, dx } = props;
  const xAxisValue = viewBox.width + viewBox.x;
  const yAxisValue = viewBox.height * 1.23;
  return (
    <text
      x={xAxisValue}
      y={yAxisValue}
      dy={dy}
      dx={dx}
      fill={'#6A7088'}
      fontSize={10}
      textAnchor={textAnchor}>
      High Risk, Low Yield
    </text>
  );
};

const CoordiChartToolTip = (props) => {
  const { payload } = props;
  if (Array.isArray(payload) && payload.length > 0) {
    const listItems = payload.map((payloadItem, payloadIndex) => {
      return (
        <li key={payloadIndex}>
          {payloadItem.name}:{' '}
          <span style={{ float: 'right' }}>
            {payloadIndex != 2
              ? Number(payloadItem.value).toFixed(2)
              : payloadItem.value}
            {payloadItem.unit}
          </span>
        </li>
      );
    });

    return (
      <div className={style.customTooltip}>
        <div className="row">
          <div className="col-sm-12">
            <ul className="list-unstyled">{listItems}</ul>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

class CoordiChart extends React.Component {
  // eslint-disable-line react/prefer-stateless-function
  constructor(props) {
    super(props);
  }

  onClusterClick = (cluster) => {
    const { circleClick, data } = this.props;

    if (circleClick) {
      if (cluster) {
        const quadrantColors = this.getQuadrantColors(cluster);

        circleClick(
          cluster,
          this.returnClustersInSameQuadrant(data, cluster),
          quadrantColors
        );
      }
    }
  };

  getClusterQuadrant = (cluster, convertToPercentage) => {
    const { xKey, yKey } = this.props;
    if (cluster) {
      let xValue, yValue;

      if (xKey) {
        xValue = cluster[xKey];
      } else {
        xValue = cluster.x;
      }

      if (yKey) {
        yValue = cluster[yKey];
      } else {
        yValue = cluster.y;
      }

      // Q1
      if (
        xValue * (convertToPercentage ? 100 : 1) >= 50 &&
        yValue * (convertToPercentage ? 100 : 1) >= 50
      ) {
        return 1;
      }

      // Q2
      if (
        xValue * (convertToPercentage ? 100 : 1) <= 50 &&
        yValue * (convertToPercentage ? 100 : 1) >= 50
      ) {
        return 2;
      }

      // Q3
      if (
        xValue * (convertToPercentage ? 100 : 1) <= 50 &&
        yValue * (convertToPercentage ? 100 : 1) <= 50
      ) {
        return 3;
      }

      // Q4
      if (
        xValue * (convertToPercentage ? 100 : 1) >= 50 &&
        yValue * (convertToPercentage ? 100 : 1) <= 50
      ) {
        return 4;
      }
    }

    return null;
  };

  getQuadrantColors = (cluster) => {
    if (cluster) {
      let clusterColor = 'gray'; // default color
      let selectedClusterColor = 'red'; // default selected color
      let quadrant = this.getClusterQuadrant(cluster);

      // Q1
      if (quadrant === 1) {
        clusterColor = 'rgba(223, 96, 87, 0.6)';
        selectedClusterColor = 'rgba(223, 96, 87, 1)';
      }

      // Q2
      if (quadrant === 2) {
        clusterColor = 'rgba(226, 152, 64, 0.6)';
        selectedClusterColor = 'rgba(226, 152, 64, 1)';
      }

      // Q3
      if (quadrant === 3) {
        clusterColor = 'rgba(113, 201, 105, 0.6)';
        selectedClusterColor = 'rgba(113, 201, 105, 1)';
      }

      // Q4
      if (quadrant === 4) {
        clusterColor = 'rgba(62, 122, 211, 0.6)';
        selectedClusterColor = 'rgba(62, 122, 211, 1)';
      }

      return {
        clusterColor,
        selectedClusterColor,
        quadrant
      };
    }

    return null;
  };

  returnClustersInSameQuadrant = (data, selectedCluster) => {
    const clusterQuadrant = this.getClusterQuadrant(selectedCluster, false);
    const clustersOfSelectedQuadrant = [];

    if (Array.isArray(data) && data.length > 0) {
      data.forEach((clusterItem) => {
        if (clusterQuadrant === this.getClusterQuadrant(clusterItem, true)) {
          clustersOfSelectedQuadrant.push(clusterItem);
        }
      });
    }

    return clustersOfSelectedQuadrant;
  };

  renderCustomCircle = ({ cx, cy, payload }) => {
    const { selectedItem, zKey, data } = this.props;
    let stroke = '#FFFFFF'; // default stroke

    if (payload && payload[zKey] !== 0 && !isNaN(payload[zKey])) {
      if (this.getQuadrantColors(payload)) {
        const { clusterColor, selectedClusterColor } =
          this.getQuadrantColors(payload);

        // get biggest clusterSize and use it to get the biggest circle
        let biggestClusterSize = 0;
        if (zKey) {
          data.forEach((clusterItem) => {
            if (clusterItem[zKey] > biggestClusterSize) {
              biggestClusterSize = clusterItem[zKey];
            }
          });
        } else {
          data.forEach((clusterItem) => {
            if (clusterItem['amt'] > biggestClusterSize) {
              biggestClusterSize = clusterItem['amt'];
            }
          });
        }

        const clusterSize = zKey ? payload[zKey] : payload.amt;
        const radiusPercentage = clusterSize / biggestClusterSize;

        return (
          <circle
            className={style.circle}
            cx={cx}
            cy={cy}
            r={
              radiusPercentage > 0.1
                ? // ? 50 * (clusterSize / biggestClusterSize)
                  50 * 0.55
                : 50 * 0.1
            }
            // edit this whenever they wanted stroke again
            stroke={
              selectedItem !== null &&
              selectedItem.clusterIndex === payload.clusterIndex
                ? stroke
                : null
            }
            strokeWidth={3}
            fill={
              selectedItem !== null &&
              selectedItem.clusterIndex === payload.clusterIndex
                ? selectedClusterColor
                : clusterColor
            }
            onClick={() => this.onClusterClick(payload)}></circle>
        );
      }
    }

    return null;
  };

  render() {
    const { selectedItem, data, xKey, yKey, zKey } = this.props;
    const margin = { top: 40, right: 20, bottom: 20, left: 20 };
    const activeQuadrant = this.getClusterQuadrant(selectedItem);

    if (data) {
      const formattedData = data.map((dataItem) => {
        const formattedDataItem = { ...dataItem };

        if (xKey) {
          formattedDataItem[xKey] = dataItem[xKey];
        }

        if (yKey) {
          formattedDataItem[yKey] = dataItem[yKey];
        }

        return formattedDataItem;
      });

      return (
        <div style={{ height: '400px', width: '100%' }}>
          <ResponsiveContainer height="100%" width="100%">
            <ScatterChart data={formattedData} margin={margin} width="491px">
              <CartesianGrid stroke="#D0D7E3" strokeDasharray="3 3" />

              <XAxis
                name="Customer Risk Score"
                dataKey={xKey ? xKey : 'x'}
                type="number"
                domain={[0, 100]}
                ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}>
                <Label
                  style={{
                    textAnchor: 'middle',
                    fill: '#6A7088',
                    font: '500 14px Inter'
                  }}
                  value="Customer Risk Score"
                  offset={-20}
                  position="insideBottom"
                />
              </XAxis>
              <YAxis
                name="Alerts Yield"
                dataKey={yKey ? yKey : 'y'}
                type="number"
                unit="%"
                domain={[0, 100]}>
                <Label
                  style={{
                    textAnchor: 'middle',
                    fill: '#6A7088',
                    font: '500 14px Inter'
                  }}
                  value="Alerts Yield"
                  offset={0}
                  angle={-90}
                  position="insideLeft"
                />
              </YAxis>
              <ZAxis
                name="Number of Customers"
                dataKey={zKey ? zKey : 'amt'}
                type="number"
              />
              <Tooltip
                active={true}
                content={<CoordiChartToolTip payload={selectedItem} />}
              />

              <ReferenceLine
                x={52}
                label="Q4"
                stroke="#fff"
                label={
                  <Q4ReferenceLabel
                    fill={activeQuadrant === 4 ? '#21A7C7' : '#556069'}
                  />
                }
              />
              <ReferenceLine
                x={43}
                label="Q3"
                stroke="#fff"
                label={
                  <Q3ReferenceLabel
                    fill={activeQuadrant === 3 ? '#21A7C7' : '#556069'}
                  />
                }
              />
              <ReferenceLine
                x={43}
                label="Q2"
                stroke="#fff"
                label={
                  <Q2ReferenceLabel
                    fill={activeQuadrant === 2 ? '#21A7C7' : '#556069'}
                  />
                }
              />
              <ReferenceLine
                x={52}
                label="Q1"
                stroke="#fff"
                label={
                  <Q1ReferenceLabel
                    fill={activeQuadrant === 1 ? '#21A7C7' : '#556069'}
                  />
                }
              />
              {/* <ReferenceLine x={65} y={65} label="Q4" stroke="#99A6B5"/> */}

              <ReferenceLine x={2} stroke="#fff" label={<TopLeftLabel />} />
              <ReferenceLine x={76} stroke="#fff" label={<TopRightLabel />} />
              <ReferenceLine x={3} stroke="#fff" label={<BottomLeftLabel />} />
              <ReferenceLine
                x={76}
                stroke="#fff"
                label={<BottomRightLabel />}
              />

              <ReferenceLine x={50} stroke="#99A6B5" />
              <ReferenceLine y={50} stroke="#99A6B5" />
              <Scatter
                shape={this.renderCustomCircle}
                data={formattedData}
                fill="#8884d8"
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      );
    }

    return null;
  }
}

CoordiChart.propTypes = {
  circleClick: PropTypes.func
};

export default CoordiChart;
